// Module 30 — the pure half of the grouping rule-set editors (ORP-18990).
//
// Everything here was inline in v3's `GroupModal.vue` / `CustomGroupModal.vue`, tangled with
// axios, the global loader and swal. Lifting it out is what makes the D-30-4 fix testable.
import type {
  CustomGroup,
  CustomGroupDraft,
  GroupRuleBranch,
  GroupRuleLeaf,
  GroupRuleNode,
  GroupRuleOperator,
  GroupRuleComparator,
} from '../models/grouping.types'

/**
 * The comparators a grouping condition may use, in v3's order
 * (`Orphex-New-Front/src/utils/helpers.js:316-343`). `is_empty`/`is_not_empty` are in 08's
 * `MATCH_MODES` but were never offered here, so they stay out: this is a wire vocabulary the
 * backend rule evaluator accepts, not a UI preference.
 */
export const GROUP_MATCH_MODES: readonly GroupRuleComparator[] = [
  'starts_with',
  'contains',
  'not_contains',
  'ends_with',
  'regex',
  'not_regex',
  'equals',
  'not_equals',
  'in',
  'not_in',
  'is',
  'is_not',
  'less_than',
  'less_than_or_equal_to',
  'greater_than',
  'greater_than_or_equal_to',
  'between',
]

/**
 * i18n key for a comparator, inside module 30's own namespace.
 *
 * The keys are camelCase because `@intlify/vue-i18n/key-format-style` is an ERROR in this repo;
 * the wire vocabulary itself stays snake_case. 08 keeps the same split in
 * `reports/service/reportFilterModes.ts:20-38` — the labels are not reused from there because
 * `reports`' message bundle is registered on 08's routes, not on the workspace-settings route this
 * editor lives on.
 */
const LABEL_SUFFIX_BY_MODE: Readonly<Record<string, string>> = {
  starts_with: 'startsWith',
  contains: 'contains',
  not_contains: 'notContains',
  ends_with: 'endsWith',
  regex: 'regex',
  not_regex: 'notRegex',
  equals: 'equals',
  not_equals: 'notEquals',
  in: 'in',
  not_in: 'notIn',
  is: 'is',
  is_not: 'isNot',
  less_than: 'lessThan',
  less_than_or_equal_to: 'lessThanOrEqualTo',
  greater_than: 'greaterThan',
  greater_than_or_equal_to: 'greaterThanOrEqualTo',
  between: 'between',
}

export function matchModeLabelKey(mode: GroupRuleComparator): string {
  return `workspaceDefinitions.grouping.matchModes.${LABEL_SUFFIX_BY_MODE[mode] ?? mode}`
}

/**
 * Per-level grouping caps, READ FROM THE BACKEND (BE-30-09 / ORP-20374, now shipped).
 *
 * v3 hardcoded 6 per level and 8 for campaign (`Grouping.vue:118-119`) and the first v4 port kept
 * those constants with a note that the wire endpoint was still pending. It is not pending any
 * more, and the constants were never right: the cap lives in the backend's DB-editable
 * `configurations_metadata` and production reads campaign 8 · adgroup 4 · product 4 · ad 3. A
 * hardcoded 6 therefore OVER-offers on ad, adgroup and product — the user gets a save-time
 * `ValidationError` where the option should simply have been unavailable — and UNDER-offers on
 * `creative_image`/`creative_video`, which have no config row and are unbounded server-side.
 *
 * `null` means "no ceiling", and it is returned for two different reasons that must behave the
 * same way: the entity has no configuration row (the backend's own rule — absence is never zero),
 * or this client has not read the config yet. Neither is a licence to invent a number; blocking a
 * legitimate slot is worse than offering one the server will refuse, and `SaveGroupingFlow`
 * already surfaces that refusal verbatim.
 */
export type GroupingCaps = Readonly<Record<string, number>> | null

export function capForLevel(caps: GroupingCaps, level: string): number | null {
  return caps?.[level] ?? null
}

/** Has `level` room for one more grouping, given `used` existing ones? */
export function levelHasFreeSlot(caps: GroupingCaps, level: string, used: number): boolean {
  const cap = capForLevel(caps, level)
  return cap === null || used < cap
}

/** The levels that may carry a grouping at all (`Grouping.vue:120`). */
export const ALLOWED_GROUPING_LEVELS: readonly string[] = [
  'ad',
  'adgroup',
  'campaign',
  'creative_image',
  'creative_video',
  'product',
]

export function isRuleBranch(node: GroupRuleNode | null): node is GroupRuleBranch {
  return node !== null && Array.isArray((node as GroupRuleBranch).children)
}

export function emptyRuleLeaf(): GroupRuleLeaf {
  return { comparator: null, field: null, type: 'dimension', value: null }
}

export function emptyRuleSet(): GroupRuleBranch {
  return { operator: 'AND', children: [emptyRuleLeaf()] }
}

/**
 * The wire shape of a rule tree (`GroupModal.vue:276-292`).
 *
 * `not_in` is the one comparator the backend wants as a list, so a comma-separated string typed
 * into a single value field is split here — exactly as the legacy did at `:289`.
 */
export function formatRules(node: GroupRuleNode | null): GroupRuleNode | null {
  if (node === null) return null
  if (isRuleBranch(node)) {
    return {
      operator: node.operator,
      children: node.children
        .map((child) => formatRules(child))
        .filter((child): child is GroupRuleNode => child !== null),
    }
  }
  return {
    comparator: node.comparator,
    field: node.field,
    type: 'dimension',
    value:
      node.comparator === 'not_in' && typeof node.value === 'string'
        ? node.value.split(',')
        : node.value,
  }
}

/** True when any leaf is missing its dimension or its value (`CustomGroupModal.vue:85-94`). */
export function hasEmptyRule(node: GroupRuleNode | null): boolean {
  if (node === null) return false
  if (isRuleBranch(node)) return node.children.some((child) => hasEmptyRule(child))
  return !node.field || node.value === null || node.value === '' || node.value === undefined
}

/**
 * Collapse a branch into its parent when both carry the same operator, and drop empty branches.
 * Ported from `CustomGroupRuleSet.vue:137-158` so the editor and its tests share one normaliser.
 */
export function flattenSameOperatorGroups(node: GroupRuleNode | null): GroupRuleNode | null {
  if (node === null) return null
  if (!isRuleBranch(node)) return { ...node }

  const normalised = node.children
    .map((child) => flattenSameOperatorGroups(child))
    .filter((child): child is GroupRuleNode => child !== null)

  const hoisted: GroupRuleNode[] = []
  for (const child of normalised) {
    if (isRuleBranch(child) && child.operator === node.operator) hoisted.push(...child.children)
    else hoisted.push(child)
  }

  return hoisted.length === 0 ? null : { operator: node.operator, children: hoisted }
}

/** A rule tree always has a branch at the root, so the editor never special-cases a bare leaf. */
export function toRuleSet(
  node: GroupRuleNode | null,
  operator: GroupRuleOperator = 'AND',
): GroupRuleBranch {
  const flattened = flattenSameOperatorGroups(node)
  if (flattened === null) return emptyRuleSet()
  return isRuleBranch(flattened) ? flattened : { operator, children: [flattened] }
}

/**
 * What to show the user about a custom group that did not persist — the information v3 destroyed.
 *
 * Read STRUCTURALLY on purpose: module 30's boundary spec keeps the shared transport module out of
 * everything but `api/`, and `appHttp` already guarantees only an `ApiError` escapes it, so the fields
 * this needs (`detail`, `i18nKey`) are always present. `detail` is empty for a failure that never
 * reached the server, so the caller falls back to the typed `errors.http.<kind>` copy instead of
 * pasting "Network Error" into a sentence about conditions.
 */
export interface CustomGroupFailureDescription {
  detail: string
  i18nKey: string
}

export function describeCustomGroupFailure(cause: unknown): CustomGroupFailureDescription {
  const error = (cause ?? {}) as { detail?: unknown; i18nKey?: unknown; kind?: unknown }
  const i18nKey = typeof error.i18nKey === 'string' ? error.i18nKey : 'errors.http.unknown'
  const detail = typeof error.detail === 'string' ? error.detail : ''
  return { i18nKey, detail: error.kind === 'network' ? '' : detail }
}

/** A custom group that already exists on the backend, so its id is known. */
export type PersistedCustomGroup = CustomGroup & { id: number }

export interface CustomGroupDiff {
  created: CustomGroup[]
  updated: PersistedCustomGroup[]
  deleted: PersistedCustomGroup[]
}

function isPersisted(group: CustomGroup): group is PersistedCustomGroup {
  return group.id !== undefined
}

function toWire(draft: CustomGroupDraft, index: number): CustomGroup {
  return {
    ...(draft.id === undefined ? {} : { id: draft.id }),
    name: draft.name,
    // v3 wrote the name into `value` too (`GroupModal.vue:231, :246`); the backend keys reports
    // off `value`, so this is contract, not duplication.
    value: draft.name,
    rule_definition: formatRules(draft.rule_definition),
    // 1-based, and it follows the editor's order — the drag handle is what reorders reports.
    sort_order: index + 1,
  }
}

/**
 * Split the editor's list against the persisted one.
 *
 * v3 did this inline as three `forEach` loops that each pushed straight into one `Promise.all`
 * fan-out (`GroupModal.vue:206-258`), which is why a single failure took the whole batch's error
 * reporting with it.
 */
export function diffCustomGroups(
  original: readonly CustomGroup[],
  next: readonly CustomGroupDraft[],
): CustomGroupDiff {
  const keptIds = new Set(next.map((draft) => draft.id).filter((id) => id !== undefined))
  const wire = next.map(toWire)
  return {
    created: wire.filter((group) => !isPersisted(group)),
    updated: wire.filter(isPersisted),
    deleted: original.filter(isPersisted).filter((group) => !keptIds.has(group.id)),
  }
}
